import React, { useMemo } from 'react';
import { BOTTOM_INDENTS, COLORS } from '@constants';
import { Divider, Select, Typography, WhiteCover } from '@ui';
import { formatString } from '@utils';
import { getTimezone } from '@utils/getTimezone';
import { Col, Form, Row } from 'antd';
import moment from 'moment-timezone';
import { FIELD_NAMES } from '../../constants';

const Standards = ({ currentAccount, style }) => {
    const timeZone = currentAccount?.settings?.timeZoneDashboard ?? currentAccount?.timeZone;
    const timezones = useMemo(() => {
        return moment.tz
            .names()
            .filter((tz) => !tz.startsWith('Etc/GMT') && tz.includes('/') && !tz.includes('Kiev'))
            .map((tz) => {
                const tzInfo = moment.tz.zone(tz);
                const year = new Date().getFullYear();
                const offset = -tzInfo.utcOffset(Date.UTC(year, 0, 1));
                const gmtString = getTimezone({ currentAccount, tzLabel: tz });

                const cleanTz = tz.replace(/_/g, ' ');
                return { tz, label: `${gmtString} ${cleanTz}`, offset };
            })
            .sort((a, b) => a.offset - b.offset);
    }, [currentAccount]);

    return (
        <WhiteCover
            data-e2e='company-info_standards-and-formats_section'
            title='Standards and formats'
            style={{ position: 'relative', paddingBottom: 40, ...style }}
        >
            <Typography.Text
                data-e2e='company-info_standards-and-formats_description'
                type='body2'
                color={COLORS.$gray60Black}
                style={BOTTOM_INDENTS.XL}
            >
                Standards and formats are used to calculate product prices, shipping weights, order times, and more.
            </Typography.Text>
            <Row gutter={[16, 16]} align='middle'>
                <Col span={12}>
                    <Typography.Text data-e2e='company-info_time-zone_block' type='badgeSmall'>
                        Time zone
                    </Typography.Text>
                </Col>
                <Col span={12} data-e2e='company-info_time-zone_dropdown'>
                    <Form.Item
                        data-e2e='company-info_time-zone_input'
                        name={FIELD_NAMES.TIMEZONE_DASHBOARD}
                        initialValue={timeZone}
                        style={{ margin: 0 }}
                    >
                        <Select
                            optionFilterProp='label'
                            showSearch
                            options={timezones.map(({ tz, label }) => ({ label, value: tz }))}
                            optionRender={(option) => (
                                <div data-e2e={`company-info_time-zone-${formatString(option.value)}_option`}>
                                    {option.label}
                                </div>
                            )}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Divider margin='40px 0' />
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Typography.Text data-e2e='company-info_units_block' type='badgeSmall'>
                        Units
                    </Typography.Text>
                </Col>
                <Col span={12}>
                    <Typography.Title data-e2e='company-info_units_value' align='end' level={3}>
                        {currentAccount.unitSystem}
                    </Typography.Title>
                </Col>
            </Row>
            <Divider margin='40px 0' />
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Typography.Text data-e2e='company-info_country_block' type='badgeSmall'>
                        Country
                    </Typography.Text>
                </Col>
                <Col span={12}>
                    <Typography.Title data-e2e='company-info_country_value' align='end' level={3}>
                        {`${currentAccount.country} (${currentAccount.countryCode})`}
                    </Typography.Title>
                </Col>
            </Row>
            <Divider margin='40px 0' />
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Typography.Text data-e2e='company-info_currency_block' type='badgeSmall'>
                        Currency
                    </Typography.Text>
                </Col>
                <Col span={12}>
                    <Typography.Title data-e2e='company-info_currency_value' align='end' level={3}>
                        {currentAccount.currency}
                    </Typography.Title>
                </Col>
            </Row>
            <Divider margin='40px 0' />
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Typography.Text data-e2e='company-info_default-weight_block' type='badgeSmall'>
                        Default weight
                    </Typography.Text>
                </Col>
                <Col span={12}>
                    <Typography.Title data-e2e='company-info_default-weight_value' align='end' level={3}>
                        {currentAccount.weightUnit}
                    </Typography.Title>
                </Col>
            </Row>
        </WhiteCover>
    );
};
export default Standards;
